import React from "react";
import { QRCode } from "react-qrcode-logo";
import ReactDOM from "react-dom";
import { jsPDF } from "jspdf";
import brandLogo from "_assets/images/prm-logo/blue-icon.png";
import companyLogo from "../../../assets/images/PRM-Logo-PNG/PRM-Assist-Logo-Col.png";

// Helper function to generate a single QR code image with branding
const generateQrCodeImage = (location) => {
    return new Promise((resolve) => {
        const container = document.createElement("div");
        container.style.position = "absolute";
        container.style.left = "-9999px";
        document.body.appendChild(container);

        const value = JSON.stringify({
            id: location._id,
            title: location.title,
            iata: location.iata,
        });

        const QRWrapper = () => (
            <QRCode
                logoImage={brandLogo}
                value={value}
                size={400}
                fgColor="#020202ff"
                bgColor="#fff"
                title={"Scan for location details"}
                logoWidth={70}
                logoHeight={70}
                logoOpacity={1}
                logoPaddingStyle="square"
                qrStyle="square"
                eyeColor="#000000"
                quietZone={4}
                removeQrCodeBehindLogo={false}
                logoPadding={1}
            />
        );

        ReactDOM.render(<QRWrapper />, container, () => {
            setTimeout(() => {
                const qrCanvas = container.querySelector("canvas");
                if (!qrCanvas) {
                    ReactDOM.unmountComponentAtNode(container);
                    container.remove();
                    resolve(null);
                    return;
                }

                const qrImg = new Image();
                qrImg.src = qrCanvas.toDataURL("image/png");

                qrImg.onload = () => {
                    const topLogo = new Image();
                    topLogo.src = companyLogo;

                    topLogo.onload = () => {
                        const padding = 40;
                        const logoHeight = 60;
                        const logoToQrGap = 70;
                        const titleHeight = 30;

                        const totalHeaderHeight = logoHeight + logoToQrGap + titleHeight;
                        const width = qrImg.width + padding * 2;
                        const height = qrImg.height + totalHeaderHeight + padding * 2;

                        const finalCanvas = document.createElement("canvas");
                        finalCanvas.width = width;
                        finalCanvas.height = height;
                        const ctx = finalCanvas.getContext("2d");

                        // Background
                        ctx.fillStyle = "#ffffff";
                        ctx.fillRect(0, 0, width, height);

                        // Top company logo
                        const logoWidth = 300;
                        const logoX = (width - logoWidth) / 2;
                        const logoY = padding;
                        ctx.drawImage(topLogo, logoX, logoY, logoWidth, logoHeight);

                        // Title
                        ctx.fillStyle = "#164dd9";
                        ctx.font = "bold 24px Arial";
                        ctx.textAlign = "center";
                        ctx.fillText(
                            `Area: ${location.title || "QR Code"}`,
                            width / 2,
                            logoY + logoHeight + logoToQrGap - 10
                        );

                        // QR code
                        ctx.drawImage(qrImg, padding, padding + totalHeaderHeight);

                        const imageData = finalCanvas.toDataURL("image/png");

                        ReactDOM.unmountComponentAtNode(container);
                        container.remove();

                        resolve({
                            imageData,
                            title: location.title,
                            width,
                            height
                        });
                    };
                };
            }, 200);
        });
    });
};

// ─── PAX (passenger) QR code — encodes a URL for phone cameras

const PAX_BASE_URL = `${window.location.origin}/remotecheckin`;
const PAX_COLOUR = '#000000'; // black (matches CCA for consistent print quality)

const generatePaxQrCodeImage = (location) => {
    return new Promise((resolve) => {
        const container = document.createElement('div');
        container.style.position = 'absolute';
        container.style.left = '-9999px';
        document.body.appendChild(container);

        const url = `${PAX_BASE_URL}/${(location.iata || '').toUpperCase()}-${location._id}`;

        const QRWrapper = () => (
            <QRCode
                logoImage={brandLogo}
                value={url}
                size={400}
                fgColor={PAX_COLOUR}
                bgColor="#fff"
                title={"Passenger check-in"}
                logoWidth={70}
                logoHeight={70}
                logoOpacity={1}
                logoPaddingStyle="square"
                qrStyle="square"
                eyeColor={PAX_COLOUR}
                quietZone={4}
                removeQrCodeBehindLogo={false}
                logoPadding={1}
            />
        );

        ReactDOM.render(<QRWrapper />, container, () => {
            setTimeout(() => {
                const qrCanvas = container.querySelector('canvas');
                if (!qrCanvas) {
                    ReactDOM.unmountComponentAtNode(container);
                    container.remove();
                    resolve(null);
                    return;
                }

                const qrImg = new Image();
                qrImg.src = qrCanvas.toDataURL('image/png');

                qrImg.onload = () => {
                    const topLogo = new Image();
                    topLogo.src = companyLogo;

                    topLogo.onload = () => {
                        const padding = 40;
                        const logoHeight = 60;
                        const logoToQrGap = 70;
                        const titleHeight = 30;
                        const paxBadgeHeight = 28;

                        const totalHeaderHeight = logoHeight + logoToQrGap + titleHeight + paxBadgeHeight;
                        const width = qrImg.width + padding * 2;
                        const height = qrImg.height + totalHeaderHeight + padding * 2;

                        const finalCanvas = document.createElement('canvas');
                        finalCanvas.width = width;
                        finalCanvas.height = height;
                        const ctx = finalCanvas.getContext('2d');

                        // Background
                        ctx.fillStyle = '#ffffff';
                        ctx.fillRect(0, 0, width, height);

                        // Top company logo
                        const logoWidth = 300;
                        ctx.drawImage(topLogo, (width - logoWidth) / 2, padding, logoWidth, logoHeight);

                        // "PAX" badge pill
                        const badgeText = 'PAX';
                        ctx.font = 'bold 14px Arial';
                        const badgeW = ctx.measureText(badgeText).width + 24;
                        const badgeH = 24;
                        const badgeX = (width - badgeW) / 2;
                        const badgeY = padding + logoHeight + 10;
                        ctx.fillStyle = '#374151';
                        ctx.beginPath();
                        ctx.roundRect(badgeX, badgeY, badgeW, badgeH, 12);
                        ctx.fill();
                        ctx.fillStyle = '#ffffff';
                        ctx.textAlign = 'center';
                        ctx.textBaseline = 'middle';
                        ctx.fillText(badgeText, width / 2, badgeY + badgeH / 2);

                        // Area title
                        ctx.fillStyle = '#111827';
                        ctx.font = 'bold 24px Arial';
                        ctx.textBaseline = 'alphabetic';
                        ctx.fillText(
                            `Area: ${location.title || 'QR Code'}`,
                            width / 2,
                            padding + logoHeight + logoToQrGap
                        );

                        // QR code
                        ctx.drawImage(qrImg, padding, padding + totalHeaderHeight);

                        const imageData = finalCanvas.toDataURL('image/png');
                        ReactDOM.unmountComponentAtNode(container);
                        container.remove();
                        resolve({ imageData, title: location.title, width, height });
                    };
                };
            }, 200);
        });
    });
};

export const handlePaxQrCodeDownload = (locations) => {
    const list = Array.isArray(locations) ? locations : [locations];
    list.forEach((location) => {
        generatePaxQrCodeImage(location).then((result) => {
            if (!result) return;
            const link = document.createElement('a');
            link.href = result.imageData;
            link.download = `pax-qr-${location?.title || 'location'}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        });
    });
};

// Download all PAX QR codes as a single PDF
export const handlePaxQrCodeDownloadAllPdf = async (locations, onProgress = null) => {
    if (!locations || locations.length === 0) {
        alert("No locations to download");
        return;
    }

    const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
        compress: true
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    const qrWidth = 80;
    const qrHeight = 100;
    const margin = 15;
    const cols = 2;
    const rows = 2;
    const qrPerPage = cols * rows;

    const horizontalSpacing = (pageWidth - (cols * qrWidth) - (2 * margin)) / (cols - 1);
    const verticalSpacing = (pageHeight - (rows * qrHeight) - (2 * margin)) / (rows - 1);

    let currentIndex = 0;
    const totalLocations = locations.length;

    for (let i = 0; i < locations.length; i++) {
        const location = locations[i];

        if (onProgress) {
            onProgress(Math.round(((i + 1) / totalLocations) * 100));
        }

        const result = await generatePaxQrCodeImage(location);
        if (!result) continue;

        const positionOnPage = currentIndex % qrPerPage;
        const col = positionOnPage % cols;
        const row = Math.floor(positionOnPage / cols);

        const x = margin + (col * (qrWidth + horizontalSpacing));
        const y = margin + (row * (qrHeight + verticalSpacing));

        if (currentIndex > 0 && positionOnPage === 0) {
            pdf.addPage();
        }

        pdf.addImage(result.imageData, "PNG", x, y, qrWidth, qrHeight);
        currentIndex++;
    }

    const totalPages = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(10);
        pdf.setTextColor(128);
        pdf.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 10, { align: "center" });
    }

    const iata = locations[0]?.iata || "locations";
    pdf.save(`pax-qr-codes-${iata}-${new Date().toISOString().split('T')[0]}.pdf`);

    return true;
};

// ─── CCA (staff) QR code — original behaviour

// Download single location as PNG (original behavior)
export const handleQrCodeDownload = (locations) => {
    locations.forEach((location) => {
        generateQrCodeImage(location).then((result) => {
            if (!result) return;

            const downloadLink = document.createElement("a");
            downloadLink.href = result.imageData;
            downloadLink.download = `qr-code-${location?.title || "location"}.png`;
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
        });
    });
};

// Download all locations as a single PDF
export const handleQrCodeDownloadAllPdf = async (locations, onProgress = null) => {

    if (!locations || locations.length === 0) {
        alert("No locations to download");
        return;
    }

    // Create PDF - A4 size
    const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
        compress: true
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    // QR code dimensions in mm (for PDF)
    const qrWidth = 80;
    const qrHeight = 100; // Including header space
    const margin = 15;
    const cols = 2;
    const rows = 2;
    const qrPerPage = cols * rows;

    // Calculate spacing
    const horizontalSpacing = (pageWidth - (cols * qrWidth) - (2 * margin)) / (cols - 1);
    const verticalSpacing = (pageHeight - (rows * qrHeight) - (2 * margin)) / (rows - 1);

    let currentIndex = 0;
    const totalLocations = locations.length;

    for (let i = 0; i < locations.length; i++) {
        const location = locations[i];

        // Update progress
        if (onProgress) {
            onProgress(Math.round(((i + 1) / totalLocations) * 100));
        }

        // Generate QR code image
        const result = await generateQrCodeImage(location);
        if (!result) continue;

        // Calculate position on page
        const positionOnPage = currentIndex % qrPerPage;
        const col = positionOnPage % cols;
        const row = Math.floor(positionOnPage / cols);

        const x = margin + (col * (qrWidth + horizontalSpacing));
        const y = margin + (row * (qrHeight + verticalSpacing));

        // Add new page if needed (except for first QR code)
        if (currentIndex > 0 && positionOnPage === 0) {
            pdf.addPage();
        }

        // Add QR code image to PDF
        pdf.addImage(result.imageData, "PNG", x, y, qrWidth, qrHeight);

        currentIndex++;
    }

    // Add page numbers
    const totalPages = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(10);
        pdf.setTextColor(128);
        pdf.text(
            `Page ${i} of ${totalPages}`,
            pageWidth / 2,
            pageHeight - 10,
            { align: "center" }
        );
    }

    // Download PDF
    const iata = locations[0]?.iata || "locations";
    pdf.save(`qr-codes-${iata}-${new Date().toISOString().split('T')[0]}.pdf`);

    return true;
};

